import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Card, CardContent } from "@/components/ui/card"
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  Loader2,
  AlertCircle,
} from "lucide-react"
import { useLanguage } from "@/lib/i18n"
import { contactSchema, type ContactFormData } from "@/lib/types"
import { apiClient } from "@/lib/api"

export default function ContactSection() {
  const { t } = useLanguage()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    },
  })

  const selectedSubject = watch("subject")

  const contactInfo = [
    {
      icon: MapPin,
      titleKey: "contact.headquarters",
      details: ["1600 Clements Bridge Rd", "Deptford, NJ 08096"],
    },
    {
      icon: Phone,
      titleKey: "contact.phone",
      details: ["Main: (856) 845-0100", "Dispatch: (856) 845-0100"],
    },
    {
      icon: Mail,
      titleKey: "contact.email",
      details: ["info@reyestruxcorp.com", "careers@reyestruxcorp.com"],
    },
    {
      icon: Clock,
      titleKey: "contact.hours",
      details: [t("contact.operations"), t("contact.office")],
    },
  ]

  const onSubmit = async (data: ContactFormData) => {
    setSubmitError(null)
    try {
      await apiClient.contact.submit(data)
      setIsSubmitted(true)
      reset()
    } catch (error) {
      console.error("Contact form submission error:", error)
      setSubmitError(
        error instanceof Error ? error.message : "Failed to send message"
      )
    }
  }

  return (
    <section id="contact" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-primary font-semibold tracking-wider uppercase mb-4">
            {t("contact.tagline")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-balance">
            {t("contact.title")}
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            {t("contact.description")}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-4">
            {contactInfo.map((info) => (
              <Card
                key={info.titleKey}
                className="bg-card border-border hover:border-primary/50 transition-all"
              >
                <CardContent className="p-4 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <info.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-card-foreground">
                      {t(info.titleKey)}
                    </h3>
                    {info.details.map((detail) => (
                      <p key={detail} className="text-sm text-muted-foreground">
                        {detail}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Contact Form */}
          <Card className="lg:col-span-2 bg-card border-border">
            <CardContent className="p-6 md:p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-card-foreground">
                    {t("contact.sent.title")}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {t("contact.sent.desc")}
                  </p>
                  <Button onClick={() => setIsSubmitted(false)}>
                    {t("contact.sendAnother")}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Error Alert */}
                  {submitError && (
                    <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4 flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-destructive">
                          Submission Failed
                        </p>
                        <p className="text-sm text-destructive/80">
                          {submitError}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="contactName"
                        className="text-card-foreground"
                      >
                        {t("contact.fullName")} *
                      </Label>
                      <Input
                        id="contactName"
                        {...register("name")}
                        placeholder="John Doe"
                        className="bg-input border-border"
                      />
                      {errors.name && (
                        <p className="text-sm text-destructive">
                          {errors.name.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="contactEmail"
                        className="text-card-foreground"
                      >
                        {t("contact.email")} *
                      </Label>
                      <Input
                        id="contactEmail"
                        type="email"
                        {...register("email")}
                        placeholder="john@company.com"
                        className="bg-input border-border"
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="contactPhone"
                        className="text-card-foreground"
                      >
                        {t("apply.phone")}
                      </Label>
                      <Input
                        id="contactPhone"
                        type="tel"
                        {...register("phone")}
                        placeholder="(555) 123-4567"
                        className="bg-input border-border"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="contactSubject"
                        className="text-card-foreground"
                      >
                        {t("contact.subject")}
                      </Label>
                      <Select
                        value={selectedSubject}
                        onValueChange={(value) => setValue("subject", value)}
                      >
                        <SelectTrigger className="bg-input border-border">
                          <SelectValue
                            placeholder={t("contact.subjectPlaceholder")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="quote">
                            {t("contact.quote")}
                          </SelectItem>
                          <SelectItem value="services">
                            {t("contact.serviceInquiry")}
                          </SelectItem>
                          <SelectItem value="partnership">
                            {t("contact.partnership")}
                          </SelectItem>
                          <SelectItem value="careers">
                            {t("contact.careers")}
                          </SelectItem>
                          <SelectItem value="support">
                            {t("contact.support")}
                          </SelectItem>
                          <SelectItem value="other">
                            {t("contact.other")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="contactMessage"
                      className="text-card-foreground"
                    >
                      {t("contact.message")} *
                    </Label>
                    <Textarea
                      id="contactMessage"
                      {...register("message")}
                      placeholder={t("contact.messagePlaceholder")}
                      rows={5}
                      className="bg-input border-border"
                    />
                    {errors.message && (
                      <p className="text-sm text-destructive">
                        {errors.message.message}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full md:w-auto"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      t("contact.send")
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}
